// Self-hosted replacement for excalidraw-app/data/firebase.ts.
// Copied over the upstream file at image build time (see app/Dockerfile).
// Keeps the same exports, but stores collab rooms and images in the
// self-hosted storage backend (storage/server.mjs) instead of Firebase.
import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

const STORAGE_URL = (
  (import.meta.env as Record<string, string | undefined>)
    .VITE_APP_STORAGE_BACKEND_URL || "/api/v2"
).replace(/\/$/, "");

const MAX_SAVE_ATTEMPTS = 5;

// private
// -----------------------------------------------------------------------------

type StoredScene = {
  sceneVersion: number;
  iv: string;
  ciphertext: string;
};

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

const base64ToBytes = (base64: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const filePath = (prefix: string, id: string) =>
  `${STORAGE_URL}/${prefix.replace(/^\/+|\/+$/g, "")}/${id}`;

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);

  return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
  data: StoredScene,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = base64ToBytes(data.ciphertext) as Uint8Array<ArrayBuffer>;
  const iv = base64ToBytes(data.iv) as Uint8Array<ArrayBuffer>;

  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

const createSceneDocument = async (
  elements: readonly SyncableExcalidrawElement[],
  roomKey: string,
): Promise<StoredScene> => {
  const sceneVersion = getSceneVersion(elements);
  const { ciphertext, iv } = await encryptElements(roomKey, elements);
  return {
    sceneVersion,
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    iv: bytesToBase64(iv),
  };
};

const fetchRoom = async (
  roomId: string,
): Promise<{ scene: StoredScene; revision: string | null } | null> => {
  const response = await fetch(`${STORAGE_URL}/rooms/${roomId}`, {
    cache: "no-store",
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to load room (${response.status})`);
  }
  return {
    scene: (await response.json()) as StoredScene,
    revision: response.headers.get("X-Revision"),
  };
};

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return SceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

// -----------------------------------------------------------------------------

/** Only used by "Export to Excalidraw+", which is not available when self-hosted. */
export const loadFirebaseStorage = async (): Promise<never> => {
  throw new Error("Excalidraw+ export is not available on this server");
};

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(filePath(prefix, id), {
          method: "PUT",
          body: buffer as Uint8Array<ArrayBuffer>,
        });
        if (!response.ok) {
          throw new Error(`Upload failed (${response.status})`);
        }
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // bail if no room exists as there's nothing we can do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  // optimistic concurrency: read, reconcile, write if the revision is unchanged; retry if
  // someone else saved the room in between (mirrors Firestore's runTransaction)
  for (let attempt = 0; attempt < MAX_SAVE_ATTEMPTS; attempt++) {
    const existing = await fetchRoom(roomId);

    let storedScene: StoredScene;
    if (!existing) {
      storedScene = await createSceneDocument(elements, roomKey);
    } else {
      const prevStoredElements = getSyncableElements(
        restoreElements(await decryptElements(existing.scene, roomKey), null),
      );
      const reconciledElements = getSyncableElements(
        reconcileElements(
          elements,
          prevStoredElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );
      storedScene = await createSceneDocument(reconciledElements, roomKey);
    }

    const response = await fetch(`${STORAGE_URL}/rooms/${roomId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(existing
          ? existing.revision
            ? { "X-If-Revision": existing.revision }
            : {}
          : { "X-If-Absent": "1" }),
      },
      body: JSON.stringify(storedScene),
    });

    if (response.status === 412) {
      continue;
    }
    if (!response.ok) {
      throw new Error(`Failed to save room (${response.status})`);
    }

    const storedElements = getSyncableElements(
      restoreElements(await decryptElements(storedScene, roomKey), null),
    );

    SceneVersionCache.set(socket, storedElements);

    return toBrandedType<RemoteExcalidrawElement[]>(storedElements);
  }

  throw new Error("Failed to save room: too many concurrent updates");
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const existing = await fetchRoom(roomId);
  if (!existing) {
    return null;
  }
  const elements = getSyncableElements(
    restoreElements(await decryptElements(existing.scene, roomKey), null, {
      deleteInvisibleElements: true,
    }),
  );

  if (socket) {
    SceneVersionCache.set(socket, elements);
  }

  return elements;
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(filePath(prefix, id));
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
